import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AbandonedCheckout,
  AbandonedCheckoutCreatedEnvelope,
  AbandonedCheckoutDeletedEnvelope,
  AbandonedCheckoutQuery,
  AbandonedCheckoutRecoveredEnvelope,
  AbandonedCheckoutSearch,
  AbandonedCheckoutUpdatedEnvelope,
  AbandonedCheckoutsQueryBuilder,
  AddAbandonedCheckoutActivityOptions,
  AddAbandonedCheckoutActivityResponse,
  BulkDeleteAbandonedCheckoutsResponse,
  GetAbandonedCheckoutAnalyticsOptions,
  GetAbandonedCheckoutAnalyticsResponse,
  QueryAbandonedCheckoutsResponse,
  RawHttpResponse,
  SearchAbandonedCheckoutsResponse,
  addAbandonedCheckoutActivity as universalAddAbandonedCheckoutActivity,
  bulkDeleteAbandonedCheckouts as universalBulkDeleteAbandonedCheckouts,
  deleteAbandonedCheckout as universalDeleteAbandonedCheckout,
  getAbandonedCheckout as universalGetAbandonedCheckout,
  getAbandonedCheckoutAnalytics as universalGetAbandonedCheckoutAnalytics,
  queryAbandonedCheckouts as universalQueryAbandonedCheckouts,
  redirectToCheckout as universalRedirectToCheckout,
  searchAbandonedCheckouts as universalSearchAbandonedCheckouts,
  typedQueryAbandonedCheckouts as universalTypedQueryAbandonedCheckouts,
} from './ecom-v1-abandoned-checkout-abandoned-checkouts.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function getAbandonedCheckout(
  httpClient: HttpClient
): GetAbandonedCheckoutSignature {
  return (abandonedCheckoutId: string) =>
    universalGetAbandonedCheckout(
      abandonedCheckoutId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAbandonedCheckoutSignature {
  /**
   * Retrieves an abandoned checkout.
   * @param - Abandoned checkout ID.
   * @returns The requested abandoned checkout.
   */
  (abandonedCheckoutId: string): Promise<
    NonNullablePaths<
      AbandonedCheckout,
      | `_id`
      | `status`
      | `buyerInfo.visitorId`
      | `buyerInfo.memberId`
      | `buyerInfo.userId`
      | `contactDetails.vatId._id`
      | `contactDetails.vatId.type`
      | `currency`
      | `totalPrice.amount`
      | `totalPrice.convertedAmount`
      | `totalPrice.formattedAmount`
      | `totalPrice.formattedConvertedAmount`
      | `activities`
      | `activities.${number}.type`
      | `checkoutUrl`
      | `lineItems`
      | `lineItems.${number}.quantity`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.productName.original`
      | `lineItems.${number}.physicalProperties.shippable`
      | `appliedDiscounts`
      | `appliedDiscounts.${number}.coupon._id`
      | `appliedDiscounts.${number}.coupon.code`
      | `appliedDiscounts.${number}.coupon.name`
      | `appliedDiscounts.${number}.discountRule._id`
      | `appliedDiscounts.${number}.discountRule.name.original`
      | `appliedDiscounts.${number}.discountType`,
      4
    >
  >;
}

export function deleteAbandonedCheckout(
  httpClient: HttpClient
): DeleteAbandonedCheckoutSignature {
  return (abandonedCheckoutId: string) =>
    universalDeleteAbandonedCheckout(
      abandonedCheckoutId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteAbandonedCheckoutSignature {
  /**
   * Deletes an abandoned checkout.
   * @param - Id of the abandoned checkout to delete
   */
  (abandonedCheckoutId: string): Promise<void>;
}

export function bulkDeleteAbandonedCheckouts(
  httpClient: HttpClient
): BulkDeleteAbandonedCheckoutsSignature {
  return (abandonedCheckoutIds: string[]) =>
    universalBulkDeleteAbandonedCheckouts(
      abandonedCheckoutIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteAbandonedCheckoutsSignature {
  /**
   * Deletes multiple abandoned checkouts.
   * @param - Abandoned checkout IDs to be deleted.
   */
  (abandonedCheckoutIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteAbandonedCheckoutsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      4
    >
  >;
}

export function queryAbandonedCheckouts(
  httpClient: HttpClient
): QueryAbandonedCheckoutsSignature {
  return () =>
    universalQueryAbandonedCheckouts(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryAbandonedCheckoutsSignature {
  /**
   * Creates a query to retrieve a list of abandoned checkouts.
   *
   * The `queryAbandonedCheckouts()` function builds a query to retrieve a list of abandoned checkouts and returns a `ResultsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `ResultsQueryBuilder` functions onto the query. `ResultsQueryBuilder` functions enable you to sort, filter, and control the results `queryAbandonedCheckouts()` returns.
   */
  (): AbandonedCheckoutsQueryBuilder;
}

export function typedQueryAbandonedCheckouts(
  httpClient: HttpClient
): TypedQueryAbandonedCheckoutsSignature {
  return (query: AbandonedCheckoutQuery) =>
    universalTypedQueryAbandonedCheckouts(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryAbandonedCheckoutsSignature {
  /** */
  (query: AbandonedCheckoutQuery): Promise<
    NonNullablePaths<
      QueryAbandonedCheckoutsResponse,
      | `results`
      | `results.${number}._id`
      | `results.${number}.status`
      | `results.${number}.buyerInfo.visitorId`
      | `results.${number}.buyerInfo.memberId`
      | `results.${number}.buyerInfo.userId`
      | `results.${number}.contactDetails.vatId._id`
      | `results.${number}.contactDetails.vatId.type`
      | `results.${number}.currency`
      | `results.${number}.totalPrice.amount`
      | `results.${number}.totalPrice.convertedAmount`
      | `results.${number}.totalPrice.formattedAmount`
      | `results.${number}.totalPrice.formattedConvertedAmount`
      | `results.${number}.checkoutUrl`
      | `abandonedCheckouts`
      | `abandonedCheckouts.${number}._id`
      | `abandonedCheckouts.${number}.status`
      | `abandonedCheckouts.${number}.buyerInfo.visitorId`
      | `abandonedCheckouts.${number}.buyerInfo.memberId`
      | `abandonedCheckouts.${number}.buyerInfo.userId`
      | `abandonedCheckouts.${number}.contactDetails.vatId._id`
      | `abandonedCheckouts.${number}.contactDetails.vatId.type`
      | `abandonedCheckouts.${number}.currency`
      | `abandonedCheckouts.${number}.totalPrice.amount`
      | `abandonedCheckouts.${number}.totalPrice.convertedAmount`
      | `abandonedCheckouts.${number}.totalPrice.formattedAmount`
      | `abandonedCheckouts.${number}.totalPrice.formattedConvertedAmount`
      | `abandonedCheckouts.${number}.checkoutUrl`,
      4
    >
  >;
}

export function searchAbandonedCheckouts(
  httpClient: HttpClient
): SearchAbandonedCheckoutsSignature {
  return (search: AbandonedCheckoutSearch) =>
    universalSearchAbandonedCheckouts(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchAbandonedCheckoutsSignature {
  /**
   * Retrieves a list of abandoned checkouts, given the provided paging, filtering, search details and sorting. Up to 100 abandoned checkouts can be returned per request.
   * @param - Search options.
   */
  (search: AbandonedCheckoutSearch): Promise<
    NonNullablePaths<
      SearchAbandonedCheckoutsResponse,
      | `abandonedCheckouts`
      | `abandonedCheckouts.${number}._id`
      | `abandonedCheckouts.${number}.status`
      | `abandonedCheckouts.${number}.buyerInfo.visitorId`
      | `abandonedCheckouts.${number}.buyerInfo.memberId`
      | `abandonedCheckouts.${number}.buyerInfo.userId`
      | `abandonedCheckouts.${number}.contactDetails.vatId._id`
      | `abandonedCheckouts.${number}.contactDetails.vatId.type`
      | `abandonedCheckouts.${number}.currency`
      | `abandonedCheckouts.${number}.totalPrice.amount`
      | `abandonedCheckouts.${number}.totalPrice.convertedAmount`
      | `abandonedCheckouts.${number}.totalPrice.formattedAmount`
      | `abandonedCheckouts.${number}.totalPrice.formattedConvertedAmount`
      | `abandonedCheckouts.${number}.checkoutUrl`,
      4
    >
  >;
}

/** @internal */
export function addAbandonedCheckoutActivity(
  httpClient: HttpClient
): AddAbandonedCheckoutActivitySignature {
  return (
    abandonedCheckoutId: string,
    options?: AddAbandonedCheckoutActivityOptions
  ) =>
    universalAddAbandonedCheckoutActivity(
      abandonedCheckoutId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddAbandonedCheckoutActivitySignature {
  /**
   * Adds a new abandoned checkout activity.
   * @param - Id of the abandoned checkout to update
   */
  (
    abandonedCheckoutId: string,
    options?: AddAbandonedCheckoutActivityOptions
  ): Promise<
    NonNullablePaths<
      AddAbandonedCheckoutActivityResponse,
      | `abandonedCheckout._id`
      | `abandonedCheckout.status`
      | `abandonedCheckout.buyerInfo.visitorId`
      | `abandonedCheckout.buyerInfo.memberId`
      | `abandonedCheckout.buyerInfo.userId`
      | `abandonedCheckout.contactDetails.vatId._id`
      | `abandonedCheckout.contactDetails.vatId.type`
      | `abandonedCheckout.currency`
      | `abandonedCheckout.totalPrice.amount`
      | `abandonedCheckout.totalPrice.convertedAmount`
      | `abandonedCheckout.totalPrice.formattedAmount`
      | `abandonedCheckout.totalPrice.formattedConvertedAmount`
      | `abandonedCheckout.activities`
      | `abandonedCheckout.activities.${number}.type`
      | `abandonedCheckout.checkoutUrl`
      | `abandonedCheckout.lineItems`
      | `abandonedCheckout.lineItems.${number}.quantity`
      | `abandonedCheckout.lineItems.${number}.catalogReference.catalogItemId`
      | `abandonedCheckout.lineItems.${number}.catalogReference.appId`
      | `abandonedCheckout.lineItems.${number}.productName.original`
      | `abandonedCheckout.lineItems.${number}.physicalProperties.shippable`
      | `abandonedCheckout.appliedDiscounts`
      | `abandonedCheckout.appliedDiscounts.${number}.coupon._id`
      | `abandonedCheckout.appliedDiscounts.${number}.coupon.code`
      | `abandonedCheckout.appliedDiscounts.${number}.coupon.name`
      | `abandonedCheckout.appliedDiscounts.${number}.discountRule._id`
      | `abandonedCheckout.appliedDiscounts.${number}.discountRule.name.original`
      | `abandonedCheckout.appliedDiscounts.${number}.discountType`,
      5
    >
  >;
}

export function getAbandonedCheckoutAnalytics(
  httpClient: HttpClient
): GetAbandonedCheckoutAnalyticsSignature {
  return (
    startDate: string,
    options?: NonNullablePaths<
      GetAbandonedCheckoutAnalyticsOptions,
      `endDate`,
      0
    >
  ) =>
    universalGetAbandonedCheckoutAnalytics(
      startDate,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAbandonedCheckoutAnalyticsSignature {
  /**
   * Returns site-level aggregated analytics for the site's abandoned checkouts within a date range.
   *
   * A "recoverable" checkout is an abandoned checkout still eligible to be won back; a "recovered"
   * checkout is one the customer later returned to and completed. The site is resolved from the
   * request context, and the date range is interpreted in the site's time zone.
   *
   * Use `metrics` to compute only a subset of the values, and `statuses` to restrict the aggregation
   * to checkouts in specific recovery states.
   * @param - First day to include in the aggregation, inclusive, as a `YYYY-MM-DD` calendar date
   * (e.g. `2022-06-01`) interpreted in the site's time zone.
   * @returns Site-level aggregate over the requested date range. Each field is populated only when its metric was
   * requested in `metrics` (or `metrics` was left empty); metrics that were not requested are left unset.
   * Monetary amounts are decimal strings (e.g. `"1250.90"`) denominated in `currency`.
   */
  (
    startDate: string,
    options?: NonNullablePaths<
      GetAbandonedCheckoutAnalyticsOptions,
      `endDate`,
      0
    >
  ): Promise<
    NonNullablePaths<GetAbandonedCheckoutAnalyticsResponse, `currency`, 0>
  >;
}

export function redirectToCheckout(
  httpClient: HttpClient
): RedirectToCheckoutSignature {
  return (abandonedCheckoutId: string, metasiteId: string) =>
    universalRedirectToCheckout(
      abandonedCheckoutId,
      metasiteId,
      // @ts-ignore
      { httpClient }
    );
}

interface RedirectToCheckoutSignature {
  /**
   * Redirects an abandoned checkout to its checkout page.
   * @param - ID of the abandoned checkout.
   * @param - Metasite ID of the site associated with the abandoned checkout.
   */
  (abandonedCheckoutId: string, metasiteId: string): Promise<
    NonNullablePaths<
      RawHttpResponse,
      `body` | `headers` | `headers.${number}.key` | `headers.${number}.value`,
      2
    >
  >;
}

export const onAbandonedCheckoutCreated = /*#__PURE__*/ EventDefinition(
  'wix.ecom.v1.abandoned_checkout_created',
  true,
  (event: AbandonedCheckoutCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.checkoutRecoveredDate' },
            { path: 'entity.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.lineItems.physicalProperties.weight' },
            { path: 'entity.appliedDiscounts.merchantDiscount.percentage' },
          ],
        },
      ])
    )
)<AbandonedCheckoutCreatedEnvelope>();
export const onAbandonedCheckoutDeleted = /*#__PURE__*/ EventDefinition(
  'wix.ecom.v1.abandoned_checkout_deleted',
  true,
  (event: AbandonedCheckoutDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.checkoutRecoveredDate' },
            { path: 'entity.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.lineItems.physicalProperties.weight' },
            { path: 'entity.appliedDiscounts.merchantDiscount.percentage' },
          ],
        },
      ])
    )
)<AbandonedCheckoutDeletedEnvelope>();
export const onAbandonedCheckoutRecovered = /*#__PURE__*/ EventDefinition(
  'wix.ecom.v1.abandoned_checkout_recovered',
  true,
  (event: AbandonedCheckoutRecoveredEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.abandonedCheckout.createdDate' },
            { path: 'data.abandonedCheckout.updatedDate' },
            { path: 'data.abandonedCheckout.checkoutRecoveredDate' },
            { path: 'data.abandonedCheckout.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.abandonedCheckout.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.abandonedCheckout.lineItems.physicalProperties.weight',
            },
            {
              path: 'data.abandonedCheckout.appliedDiscounts.merchantDiscount.percentage',
            },
          ],
        },
      ])
    )
)<AbandonedCheckoutRecoveredEnvelope>();
export const onAbandonedCheckoutUpdated = /*#__PURE__*/ EventDefinition(
  'wix.ecom.v1.abandoned_checkout_updated',
  true,
  (event: AbandonedCheckoutUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.checkoutRecoveredDate' },
            { path: 'entity.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.lineItems.physicalProperties.weight' },
            { path: 'entity.appliedDiscounts.merchantDiscount.percentage' },
          ],
        },
      ])
    )
)<AbandonedCheckoutUpdatedEnvelope>();

export {
  AbandonedCheckout,
  AbandonedCheckoutCreatedEnvelope,
  AbandonedCheckoutDeletedEnvelope,
  AbandonedCheckoutQuerySpec,
  AbandonedCheckoutRecovered,
  AbandonedCheckoutRecoveredEnvelope,
  AbandonedCheckoutSearchSpec,
  AbandonedCheckoutUpdatedEnvelope,
  AbandonedCheckoutsQueryBuilder,
  AbandonedCheckoutsQueryResult,
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Activity,
  ActivityType,
  AddAbandonedCheckoutActivityOptions,
  AddAbandonedCheckoutActivityRequest,
  AddAbandonedCheckoutActivityResponse,
  AddressLocation,
  ApplicationError,
  AppliedDiscount,
  AppliedDiscountDiscountSourceOneOf,
  BaseEventMetadata,
  BulkAbandonedCheckoutResult,
  BulkActionMetadata,
  BulkDeleteAbandonedCheckoutsRequest,
  BulkDeleteAbandonedCheckoutsResponse,
  BuyerInfo,
  Cancel,
  CartAbandonedEvent,
  CartRecoveredEvent,
  CatalogReference,
  Color,
  CommonCursorPaging,
  CommonCursors,
  CommonPaging,
  CommonPagingMetadataV2,
  CommonSortOrder,
  CommonSorting,
  Complete,
  Coupon,
  CursorPaging,
  Cursors,
  DeleteAbandonedCheckoutRequest,
  DeleteAbandonedCheckoutResponse,
  DescriptionLine,
  DescriptionLineDescriptionLineValueOneOf,
  DescriptionLineName,
  DescriptionLineType,
  DescriptionLineValueOneOf,
  Dimensions,
  DimensionsUnit,
  DiscountRule,
  DiscountRuleName,
  DiscountType,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FullAddressContactDetails,
  GetAbandonedCheckoutAnalyticsOptions,
  GetAbandonedCheckoutAnalyticsRequest,
  GetAbandonedCheckoutAnalyticsResponse,
  GetAbandonedCheckoutRequest,
  GetAbandonedCheckoutResponse,
  HeadersEntry,
  IdentificationData,
  IdentificationDataIdOneOf,
  Identity,
  ItemMetadata,
  LineItem,
  MerchantDiscount,
  MessageEnvelope,
  Metric,
  Mode,
  MultiCurrencyPrice,
  Paging,
  PagingMetadataV2,
  PhysicalProperties,
  PlainTextValue,
  ProductName,
  QueryAbandonedCheckoutsRequest,
  QueryAbandonedCheckoutsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RawHttpResponse,
  RedirectToCheckoutRequest,
  Reschedule,
  RestoreInfo,
  Search,
  SearchAbandonedCheckoutsRequest,
  SearchAbandonedCheckoutsResponse,
  SearchDetails,
  SearchPagingMethodOneOf,
  SortOrder,
  Sorting,
  Status,
  Task,
  TaskAction,
  TaskActionActionOneOf,
  TaskKey,
  Totals,
  V1BuyerInfo,
  V1BuyerInfoIdOneOf,
  VatId,
  VatType,
  WebhookIdentityType,
  utils,
} from './ecom-v1-abandoned-checkout-abandoned-checkouts.universal.js';
